// Fixed version — solves: guardian deadlock, dead observers, dropped renders, drawer-open blind spot,
// AND stale GWP remaining in cart when discount terms no longer met (enforced against Shopify's
// native discount signal — if the GWP line isn't discounted, it gets removed, and the theme's
// cart drawer is force-refreshed so line indexes stay in sync).
// PLUS: mobile-only collapsible gift slider (≤450px) with a centered toggle pill on the bottom edge.
// PLUS: per-page-load randomised slider order (new shuffle each hard refresh, stable across re-renders).
// 🆕 TOUCH DRAG & PROGRESS INDICATOR — slides can now be swiped/dragged on mobile
// with a visual progress bar showing position and allowing direct dragging.
// 🆕 WATCHDOG FIX: Observes document.body instead of #gwp-container so observer survives
// when theme re-renders and replaces the container node.

class SaisonGWPManger {
  constructor() {
    this.config = null;
    this.renderedIds = new Set();
    this.productCache = new Map();
    this.isProcessing = false;
    this.pendingCheck = null;
    this.currentSlide = 0;
    this.sliderInterval = null;
    this.lastCartState = null;
    this.navPrevBtn = null;
    this.navNextBtn = null;
    this.totalSlidesCount = 0;
    this.slideWidthPx = 0;
    this.hasUserInteracted = false;
    this.isUpdatingDOM = false;
    this.watchdogRunning = false;      // 🔁 was guardianRunning
    this.cartUpdateQueued = false;

    this.handlersBound = false;
    this.wrapperJustChecked = false;
    this.lastRenderTime = 0;
    this.lastDiscountState = null;
    this.debounceTimer = null;
    this.needsRender = false;       // queue flag for dropped renders

    // 🆕 Mobile collapse state
    this.sliderCollapsed = null;    // null = "not yet decided"; resolves to mobile-default on first render
    this._lastWasMobile = null;     // tracks viewport class across resize events

    // 🆕 Per-page-load random order for GWP slider (randomised on each hard refresh, but stable across re-renders within the same page session).
    this._ruleSeeds = new Map();

    // 🆕 Touch drag state for slider
    this.touchStartX = null;
    this.touchEndX = null;
    this.minSwipeDistance = 50;
    this.isDragging = false;

    this._runInit();
  }

  _runInit() {
    console.log('🚀 SaisonGWPManger: Page Loaded. Initializing...');

    const configScript = document.getElementById('gwp-config-data');
    if (configScript) {
      try {
        this.config = JSON.parse(configScript.textContent);
        console.log('Saison GWP: Config loaded.');
      } catch (e) {
        console.error('Saison GWP: Config parse error', e);
      }
    } else {
      console.error('Saison GWP: Config script NOT FOUND.');
      return;
    }

    this.bindEvents();

    console.log('Saison GWP: Calling render...');
    this.checkAndRenderGWPs();
  }

  bindEvents() {
    if (this.handlersBound) {
      console.log('⚠️ bindEvents skipped - already bound');
      return;
    }

    console.log('🔧 bindEvents: Binding events');

    // Initialize discount state BEFORE observers start
    this.lastDiscountState = document.querySelector('.wt-cart__item-discount') !== null;

    // 🆕 Disconnect old watchdog (replaces guardianObserver + protectObserver)
    if (this.watchdogObserver) { 
      this.watchdogObserver.disconnect(); 
      this.watchdogObserver = null; 
    }
    if (this.guardianObserver) { this.guardianObserver.disconnect(); this.guardianObserver = null; }
    if (this.protectObserver) { this.protectObserver.disconnect(); this.protectObserver = null; }

    const container = document.getElementById('gwp-container');
    if (!container) return;

    // 🆕 WATCHDOG OBSERVER — watches document.body for #gwp-container replacement
    // Old design observed #gwp-container itself, so when theme replaced that node,
    // the observer became a zombie (detached nodes never fire mutations).
    this.watchdogRenderTimer = null;

    const watchdogObserver = new MutationObserver(() => {
      const liveContainer = document.getElementById('gwp-container');

      // Case 1: theme swapped the container node — rebind + rebuild
      if (liveContainer !== container) {
        console.log('🛡️ Watchdog: #gwp-container replaced by theme — recovering');
        watchdogObserver.disconnect();

        if (this.watchdogRenderTimer) clearTimeout(this.watchdogRenderTimer);
        this.watchdogRenderTimer = setTimeout(() => {
          this.watchdogRenderTimer = null;
          this.isProcessing = false;
          this.watchdogRunning = false;
          this.handlersBound = false;
          this.watchdogObserver = null;
          this.bindEvents();         // rebind observers to fresh node
          this.checkAndRenderGWPs();
        }, 300);
        return;
      }

      // Case 2: same node, but our wrapper was wiped inside it
      const wrapper = container.querySelector('#gwp-protected-content');
      if (!wrapper && !this.isProcessing && !this.watchdogRunning) {
        if (!this.watchdogRenderTimer) {
          this.watchdogRenderTimer = setTimeout(() => {
            this.watchdogRenderTimer = null;
            if (!this.isProcessing && !this.watchdogRunning) {
              this.checkAndRenderGWPs();
            }
          }, 400);
        }
      }
    });

    // Observe document.body so we survive container replacement
    watchdogObserver.observe(document.body, { childList: true, subtree: true });
    this.watchdogObserver = watchdogObserver;

    const discountObserver = new MutationObserver(() => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      const hasDiscount = document.querySelector('.wt-cart__item-discount') !== null;

      if (this.lastDiscountState !== hasDiscount && !this.watchdogRunning && !this.isProcessing) {
        console.log(`🛡️ Discount state changed: ${this.lastDiscountState ? 'DISCOUNT REMOVED' : 'DISCOUNT ADDED'}, triggering re-render`);
        this.debounceTimer = setTimeout(() => {
          this.lastDiscountState = hasDiscount;
          this.checkAndRenderGWPs();
        }, 500);
      }
    });
    discountObserver.observe(document.body, { childList: true, subtree: true });
    this.discountObserver = discountObserver;

    const cartHandler = () => {
      if (!this.cartUpdateQueued) {
        this.cartUpdateQueued = true;
        console.log('📦 Cart Updated Event Detected! Queueing GWP Re-render.');
        setTimeout(() => {
          this.cartUpdateQueued = false;
          this.checkAndRenderGWPs();
        }, 800);
      }
    };

    if (typeof subscribe === 'function') {
      try { unsubscribe('cart-update', this._existingCartHandler || cartHandler); } catch(e) {}
      subscribe('cart-update', cartHandler);
      this._existingCartHandler = cartHandler;
    } else {
      document.removeEventListener('cart:updated', this._existingFullCartHandler || cartHandler);
      document.addEventListener('cart:updated', cartHandler);
      this._existingFullCartHandler = cartHandler;

      document.removeEventListener('cart-update', this._existingShortCartHandler || cartHandler);
      document.addEventListener('cart-update', cartHandler);
      this._existingShortCartHandler = cartHandler;
    }

    const ensureRendered = () => {
      const c = document.getElementById('gwp-container');
      const wrapper = document.getElementById('gwp-protected-content');
      if (!c || !wrapper || wrapper.children.length === 0) {
        console.log('🛡️ Drawer opened with missing/empty GWP wrapper — forcing render');
        this.checkAndRenderGWPs();
      }
    };
    document.addEventListener('cart-drawer:open', ensureRendered);
    document.addEventListener('cart-drawer:refresh', ensureRendered);
    this._ensureRendered = ensureRendered;

    // 🆕 Re-render when crossing the mobile/desktop boundary so the toggle
    // appears/disappears and the collapse state resets appropriately.
    this._lastWasMobile = this._isMobileViewport();
    
    // 🆕 IDempotent resize listener — prevents accumulation on re-binding
    if (!this._resizeListener) {
      this._resizeListener = () => {
        const nowMobile = this._isMobileViewport();
        if (nowMobile !== this._lastWasMobile) {
          this._lastWasMobile = nowMobile;
          this.checkAndRenderGWPs();
        }
      };
    }
    window.removeEventListener('resize', this._resizeListener);
    window.addEventListener('resize', this._resizeListener);

    this.handlersBound = true;
    console.log('✅ bindEvents completed');
  }

  _setupObservers() {
    console.log('🛡️ Re-attaching watchdog to fresh container node...');
    if (this.watchdogObserver) { this.watchdogObserver.disconnect(); this.watchdogObserver = null; }
    if (this.guardianObserver) { this.guardianObserver.disconnect(); this.guardianObserver = null; }
    if (this.protectObserver) { this.protectObserver.disconnect(); this.protectObserver = null; }
    
    // Don't reset handlersBound here — the watchdog handler will call bindEvents() directly
    this.watchdogObserver = null;
  }

  // 🆕 Mobile breakpoint for the collapse feature (≤450px = mobile).
  _isMobileViewport() {
    return window.matchMedia('(max-width: 450px)').matches;
  }

  // 🆕 Mobile bottom clearance for the toggle pill.
  // Collapsed: 15px keeps the pill clear of the threshold bar.
  // Expanded: 10px gives breathing room between the vendor names and the pill.
  _applyMobileCollapsePadding(wrapperEl) {
    if (!this._isMobileViewport()) {
      wrapperEl.style.removeProperty('padding-bottom');
      return;
    }
    if (this.sliderCollapsed) {
      wrapperEl.style.setProperty('padding-bottom', '15px', 'important');
    } else {
      wrapperEl.style.setProperty('padding-bottom', '10px', 'important');
    }
  }

  isDrawerOpen() {
    return document.querySelector('.wt-cart__drawer--open') !== null || document.querySelector('[aria-modal="true"]') !== null;
  }

  getFreeShippingThreshold() {
  // Setting is entered in theme editor as a plain dollar figure (e.g. 75).
  // Config JSON receives it raw; convert to cents here — single source of truth.
  let threshold = null;

  if (this.config && this.config.free_shipping_threshold != null) {
    threshold = parseFloat(this.config.free_shipping_threshold);
  } else if (typeof window.freeShippingThreshold !== 'undefined' && window.freeShippingThreshold != null) {
    threshold = parseFloat(window.freeShippingThreshold);
  }

  if (isNaN(threshold) || threshold <= 0) {
    console.warn('Saison GWP: Shipping threshold missing/invalid — check settings.shipping_threshold. Defaulting to $100.');
    return 10000;
  }

  return Math.round(threshold * 100);
}

  isDateRangeActive(startDateStr, endDateStr) {
    const now = new Date();
    if (!startDateStr && !endDateStr) return true;

    const start = startDateStr ? new Date(startDateStr) : null;
    const end = endDateStr ? new Date(endDateStr) : null;

    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  }

  getExchangeRate() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
      const rate = parseFloat(window.Shopify.currency.rate);
      if (rate > 0) return rate;
    }
    return 1.08;
  }

  getCurrentCurrency() {
    let currency = null;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currency = window.Shopify.currency.active;
    }
    if (!currency && window.cart && window.cart.currency) {
      currency = window.cart.currency;
    }

    if (currency) {
      currency = currency.toString().trim().toUpperCase();
      if (currency === 'NZD' || currency === 'NZ$' || currency.includes('NEW ZEALAND')) {
        return 'NZD';
      }
      return 'OTHER';
    }

    return 'UNKNOWN';
  }

  hasDiscountedGWPInDOM() {
    const drawerDiscount = document.querySelector('.wt-cart__item-discount');
    if (drawerDiscount) {
      console.log('💰 Discount detected in cart drawer DOM');
      return true;
    }

    const pageDiscount = document.querySelector('.cart-items .wt-cart__item-discount, .cart__item .wt-cart__item-discount');
    if (pageDiscount) {
      console.log('💰 Discount detected on cart page DOM');
      return true;
    }

    return false;
  }

  // Force the theme's cart drawer to rebuild its HTML from fresh cart state.
  // Without this, the drawer shows stale line items and the theme's delete
  // buttons operate on stale line indexes (deleting the wrong item).
  async refreshCartDrawer() {
    // 1. Ask the theme politely first (same event it fires after a manual add)
    document.body.dispatchEvent(new Event('cart-drawer:refresh', { bubbles: true }));

    // 2. Section Rendering API fallback — rebuild the drawer HTML directly.
    //    ⚠️ Set this to your drawer's section filename. Check view-source for
    //    <section id="shopify-section-cart-drawer"> — the suffix is what goes here.
    const DRAWER_SECTION = 'cart-drawer';

    try {
      const res = await fetch(`${window.location.pathname}?sections=${DRAWER_SECTION}`);
      if (!res.ok) return;
      const sections = await res.json();
      const html = sections[DRAWER_SECTION];
      if (!html) return;

      const mount = document.getElementById(`shopify-section-${DRAWER_SECTION}`);
      if (mount) {
        mount.innerHTML = html;
        console.log('🛡️ Cart drawer section re-rendered via Section Rendering API');
        // Let our own observers know the DOM changed
        document.body.dispatchEvent(new CustomEvent('cart:updated'));
        document.body.dispatchEvent(new CustomEvent('cart-update', { bubbles: true }));
      }
    } catch (e) {
      console.warn('Saison GWP: Section refresh fallback failed', e);
    }
  }

  // ELIGIBILITY ENFORCEMENT
  // Shopify's native discount is the single source of truth: when the customer
  // no longer qualifies, Shopify stops applying the discount and the GWP line
  // comes back undiscounted. Any _gwp_item line with no discount signal gets
  // removed from the cart, then the theme drawer is force-refreshed.
  async enforceGWPEligibility(cart) {
    const gwpItems = cart.items.filter(
      item => item.properties && item.properties._gwp_item === 'true'
    );
    if (gwpItems.length === 0) return false;

    const itemsToRemove = gwpItems.filter(item => {
      const isDiscounted =
        (item.total_discount || 0) > 0 ||
        (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) ||
        (item.discounts && item.discounts.length > 0);

      if (!isDiscounted) {
        console.log('🧹 GWP line is NOT discounted — discount terms no longer met:', item.title);
      }
      return !isDiscounted;
    });

    if (itemsToRemove.length === 0) {
      console.log('✅ GWP in cart is still discounted — eligible, keeping it');
      return false;
    }

    console.log(`🧹 Removing ${itemsToRemove.length} unqualified GWP item(s) — discount no longer applying`);
    for (const item of itemsToRemove) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
        if (!res.ok) console.error('Saison GWP: Failed to remove GWP', item.key);
      } catch (e) {
        console.error('Saison GWP: Error removing GWP', e);
      }
    }

    // Critical: rebuild the theme drawer so line indexes stay in sync.
    await this.refreshCartDrawer();
    return true;
  }

  async checkAndRenderGWPs() {
    // Defensive force-unlock for any state that slips through
    if (this.watchdogRunning && this.isProcessing) {
      console.warn('⚠️ Stuck state detected, forcing unlock...');
      this.watchdogRunning = false;
      this.isProcessing = false;
    }

    if (!this.config) {
      console.log('Saison GWP: Skipping - no config');
      return;
    }

    if (this.isProcessing || this.watchdogRunning) {
      console.log('Saison GWP: Busy - queuing a follow-up render');
      this.needsRender = true;
      return;
    }

    console.log('🔄 checkAndRenderGWPs called! Starting fresh render...');

    this.isProcessing = true;
    this.lastRenderTime = Date.now();
    this.isUpdatingDOM = true;

    try {
      const container = document.getElementById('gwp-container');
      if (!container) {
        console.warn('Saison GWP: Container not found. Will retry on next event.');
        return;
      }

      let cart = null;
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        cart = await response.json();
      } catch (e) {
        if (window.Shopify && window.Shopify.cart) cart = window.Shopify.cart;
        else if (window.cart) cart = window.cart;
        if (!cart) {
          console.error('No cart data.');
          return;
        }
      }

      console.log(`🛒 DEBUG: Cart Total: $${cart.total_price/100}, Items: ${cart.items.length}`);

      // ENFORCEMENT: remove any GWP line the native discount is no longer
      // applying to (customer dropped below threshold, promo expired, etc.)
      // Enforcement also rebuilds the theme drawer internally if it removes
      // anything, so no manual event dispatches are needed here.
      const removalHappened = await this.enforceGWPEligibility(cart);
      if (removalHappened) {
        try {
          const refreshed = await fetch('/cart.js');
          if (refreshed.ok) cart = await refreshed.json();
        } catch (e) {
          console.warn('Saison GWP: Refetch after cleanup failed — using previous cart snapshot');
        }
      }

      // Note: `let` (was `const`) so it's recomputed after a possible cleanup above
      let gwpItem = cart.items.find(item => item.properties && item.properties._gwp_item === 'true');
      let hasGWPInCart = !!gwpItem;
      console.log(hasGWPInCart ? '🎯 GWP in cart detected (discounted — valid)' : '📋 No GWP in cart');

      const detectedCurrency = this.getCurrentCurrency();
      const cartCurrency = cart.currency || 'Unknown';
      const isNzCustomer = (detectedCurrency === 'NZD' || (cartCurrency && cartCurrency.toString().toUpperCase().includes('NZ')));

      // 🆕 Check if regular (non-GWP) items have discounts applied
      // This indicates automatic sale discount is active — suppress all GWP messaging
      const hasSaleDiscount = cart.items.some(item => {
        if (item.properties && item.properties._gwp_item === 'true') return false;
        return (item.total_discount || 0) > 0 || (item.discounts && item.discounts.length > 0);
      });
      
      if (hasSaleDiscount) {
        console.log('🏷️ Sale discount detected on regular items');
      }

      const allRules = [];
      if (this.config.promotions) {
        for (const promo of this.config.promotions) {
          if (!promo.enabled) continue;
          if (promo.hide_from_cart_widget) continue; 
          if (isNzCustomer && promo.australia_only) continue;
          if (!this.isDateRangeActive(promo.start_date, promo.end_date)) continue;

          const qualifyingTotal = this.calculateQualifyingTotal(cart, promo.spend_vendor, promo.sitewide);
          const minSpendCents = this.parseMinSpend(promo.min_spend);
          let adjustedMinSpend = isNzCustomer ? Math.round(minSpendCents * this.getExchangeRate()) : minSpendCents;

          // 🆕 DEBUG LOG FOR NON-SITEWIDE RULES — must come AFTER adjustedMinSpend
          if (!promo.sitewide) {
            console.log(`🔍 Vendor GWP Rule: "${promo.title}"`);
            console.log(`   spend_vendor setting: "${promo.spend_vendor}"`);
            console.log(`   Cart item vendors:`, cart.items.map(i => i.vendor));
            console.log(`   Qualifying total: $${qualifyingTotal/100}`);
            console.log(`   Min spend: $${adjustedMinSpend/100}`);
            console.log(`   IsActive: ${qualifyingTotal >= adjustedMinSpend}`);
          }

          allRules.push({
            ...promo,
            qualifyingTotal,
            minSpendCents: adjustedMinSpend,
            isActive: qualifyingTotal >= adjustedMinSpend
          });
        }
      }

      const productPromises = allRules.map(promo => {
        if (this.productCache.has(promo.gwp_product_handle)) return Promise.resolve({ data: this.productCache.get(promo.gwp_product_handle), error: null });
        return fetch(`/products/${promo.gwp_product_handle}.json`)
          .then(res => res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)))
          .then(data => { this.productCache.set(promo.gwp_product_handle, data); return { data, error: null }; })
          .catch(err => ({ data: null, error: err }));
      });

      const product_results = await Promise.all(productPromises);
      const validRules = allRules.filter((_, i) => product_results[i].data !== null);

      const thresholds = [];
      if (!isNzCustomer) {
        let freeShippingThreshold = this.getFreeShippingThreshold();
        if (freeShippingThreshold < 5000 && freeShippingThreshold > 0) freeShippingThreshold *= 100;
        thresholds.push({ amount: freeShippingThreshold, label: 'Free Shipping', icon: '⛟', isActive: cart.total_price >= freeShippingThreshold, type: 'shipping' });
      }

      validRules.forEach(rule => {
        if (!rule.sitewide && rule.qualifyingTotal === 0) {
          console.log(`🚫 Skipping "${rule.title}" in thresholds — 0 qualifying items in cart`);
          return;
        }
        let minSpend = rule.minSpendCents;
        if (minSpend < 5000 && minSpend > 0) minSpend *= 100;
        let labelText = rule.title || rule.rule_title || 'Gift';
        if (rule.sitewide && !labelText) labelText = 'Sitewide Gift';
        const productName = product_results[allRules.indexOf(rule)]?.data?.product?.title || 'Gift';
        thresholds.push({ amount: minSpend, label: labelText, icon: '\u{1F381}\u{FE0E}', isActive: rule.isActive, type: 'gwp', ruleId: rule.id, productName, qualifyingTotal: rule.qualifyingTotal, sitewide: rule.sitewide, showOnCollections: rule.show_on_collections });
      });

      thresholds.sort((a, b) => a.amount - b.amount);

      // 🆕 Pass hasSaleDiscount flag to message builder
      const messageData = this.getNextRewardMessage(cart.total_price, thresholds, isNzCustomer, hasSaleDiscount);
      const activeValidRules = validRules.filter(rule => rule.isActive);

      const wrapperID = 'gwp-protected-content';
      let currentWrapper = document.getElementById(wrapperID);

      if (!currentWrapper) {
        console.log('📦 Creating new wrapper element');
        currentWrapper = document.createElement('div');
        currentWrapper.id = wrapperID;
        container.appendChild(currentWrapper);
      } else {
        console.log(`🧹 PRE-CLEANUP: Wrapper has ${currentWrapper.children.length} children - clearing all`);
        while (currentWrapper.firstChild) {
          currentWrapper.removeChild(currentWrapper.firstChild);
        }
      }

      container.style.setProperty('display', 'block', 'important');
      container.style.visibility = 'visible';
      container.style.opacity = '1';

      currentWrapper.style.setProperty('display', 'block', 'important');
      currentWrapper.style.visibility = 'visible';
      currentWrapper.style.opacity = '1';

      // 🆕 Gap below the entire widget on mobile, drawn in the drawer's page
      // background (not the widget's own background). Applied to the container
      // itself — margins on elements inside it collapse/absorb instead.
      if (this._isMobileViewport()) {
        container.style.setProperty('margin-bottom', '10px', 'important');
      } else {
        container.style.removeProperty('margin-bottom');
      }
      

      // BUILD MESSAGE CONTAINER
      if (messageData.shippingMsg.show || messageData.gwpMsg.show) {
        const messageContainer = document.createElement('div');
        messageContainer.style.paddingTop = '20px';
        messageContainer.style.width = '100%';

        if (messageData.shippingMsg.show) {
            const msgEl = document.createElement('div');
            msgEl.className = 'gwp-message gwp-shipping-msg';
            msgEl.style.fontSize = '14px';
            msgEl.style.fontWeight = '600';
            msgEl.style.color = '#333';
            msgEl.style.lineHeight = '1.4';
            msgEl.style.padding = '0 30px';
            msgEl.innerHTML = messageData.shippingMsg.text;
            messageContainer.appendChild(msgEl);

            // This applies regardless of whether progress bar renders below
            if (!messageData.gwpMsg.show) {
              msgEl.style.marginBottom = '10px';}
        }

        if (messageData.gwpMsg.show) {
            const msgEl = document.createElement('div');
            msgEl.className = 'gwp-message gwp-gwp-msg';
            msgEl.style.fontSize = '14px';
            msgEl.style.fontWeight = '500';
            msgEl.style.color = '#333';
            msgEl.style.lineHeight = '1.4';
            msgEl.style.padding = '0 30px';
            msgEl.innerHTML = messageData.gwpMsg.text;
            messageContainer.appendChild(msgEl);

            const tcLink = document.createElement('div');
            tcLink.style.fontSize = '13px';
            tcLink.style.color = '#666';
            tcLink.style.lineHeight = '1.6';
            tcLink.style.marginTop = '5px';
            if (this._isMobileViewport()) {
              tcLink.style.marginBottom = '0px';
            } else {
              tcLink.style.marginBottom = '20px';
            }
            tcLink.style.padding = '0 30px';
            tcLink.style.fontFamily = '"DM Sans", sans-serif';
            messageContainer.appendChild(tcLink);
        }

        currentWrapper.appendChild(messageContainer);
      }

      // PROGRESS BAR — hidden entirely on mobile (≤450px); threshold messages
      // below still communicate the same info.
      const progressBar = this._isMobileViewport()
        ? null
        : this.renderProgressBar(cart.total_price, thresholds, hasSaleDiscount);
      if (progressBar) {
        currentWrapper.appendChild(progressBar);
        console.log('✅ Progress bar rendered and appended');

        progressBar.style.setProperty('display', 'block', 'important');
        progressBar.style.setProperty('visibility', 'visible', 'important');
        progressBar.style.setProperty('opacity', '1', 'important');
        progressBar.style.height = '20px';
      } else {
        console.log('⚠️ No progress bar to render');
        // 🆕 ADD SPACING BELOW SHIPPING MESSAGE WHEN PROGRESS BAR IS ABSENT
        if (messageData.shippingMsg.show) {
          currentWrapper.style.setProperty('padding-bottom', '10px', 'important');
        }
      }

      // SLIDER — only render if no GWP in cart
      const hasDOMDiscount = this.hasDiscountedGWPInDOM();
      const shouldHideSlider = hasGWPInCart || hasDOMDiscount;

      if (activeValidRules.length > 0 && !shouldHideSlider) {
        console.log('Rendering slider - no GWP in cart');

        // 🆕 Shuffle rule order per page load (stable across re-renders)
        const sliderActiveRules = this._shuffledRules(activeValidRules);
        const isMobileViewport = this._isMobileViewport();

        // 🆕 MOBILE COLLAPSE STATE — persists across re-renders.
        // First render on mobile starts collapsed (saves vertical space);
        // desktop is always expanded.
        if (this.sliderCollapsed === null) this.sliderCollapsed = false;
        if (!isMobileViewport) this.sliderCollapsed = false;

        const heading = document.createElement('h3');
        heading.textContent = 'Choose your free gift:';
        heading.style.margin = '0 0 0 0';
        heading.style.fontSize = '16px';
        heading.style.fontWeight = 'normal';
        heading.style.textAlign = 'center';
        heading.style.fontFamily = 'Palatino, serif';

        const wrapperDiv = document.createElement('div');
        wrapperDiv.className = 'saison-gwp-slider-wrapper';
        wrapperDiv.style.position = 'relative';
        wrapperDiv.style.overflow = 'hidden';
        wrapperDiv.style.width = '100%';
        wrapperDiv.style.margin = '9px 0 0 0';
        wrapperDiv.style.padding = '-5px 10px 10px 10px';
        wrapperDiv.style.boxSizing = 'border-box';
        wrapperDiv.style.minHeight = this._isMobileViewport() ? '120px' : '150px';
        wrapperDiv.style.borderLeft = '20px solid #F8F8F8';
        wrapperDiv.style.borderRight = '20px solid #F8F8F8';

        const createNavButton = (char, side, onClick) => {
            const btn = document.createElement('button');
            btn.innerHTML = char;
            btn.style.position = 'absolute';
            btn.style.top = '25%';
            btn.style.transform = 'translateY(-50%)';
            btn.style.background = '#000000ff';
            btn.style.border = 'none';
            btn.style.borderRadius = '50%';
            btn.style.width = '32px';
            btn.style.height = '32px';
            btn.style.cursor = 'pointer';
            btn.style.zIndex = '20';
            btn.style.fontSize = '9px';
            btn.style.lineHeight = '32px';
            btn.style.textAlign = 'center';
            btn.style.boxShadow = 'none';
            btn.style.padding = '0';
            btn.style.boxSizing = 'border-box';
            btn.style.outline = 'none';
            btn.style.transition = 'all 0.2s ease';
            btn.style.color = '#fff';
            if (side === 'left') {
                btn.style.left = '0';
            } else {
                btn.style.right = '0';
            }
            btn.onmouseover = () => { btn.style.background = '#A4C4D2'; btn.style.color = '#FFF'; };
            btn.onmouseout = () => { btn.style.background = '#000'; btn.style.color = '#FFF'; };
            btn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); this.hasUserInteracted = true; onClick(); };
            return btn;
        };

        this.navPrevBtn = createNavButton('&#10094;', 'left', () => this.prevSlide(sliderActiveRules.length));
        this.navNextBtn = createNavButton('&#10095;', 'right', () => this.nextSlide(sliderActiveRules.length));
        wrapperDiv.appendChild(this.navPrevBtn);
        wrapperDiv.appendChild(this.navNextBtn);

        const slidesContainer = document.createElement('div');
        slidesContainer.className = 'saison-gwp-slides';
        slidesContainer.style.display = 'flex';
        slidesContainer.style.transition = 'transform 0.3s ease-in-out';
        slidesContainer.style.transform = 'translateX(0)';
        slidesContainer.style.margin = '-8px 0 0 0';
        slidesContainer.style.padding = '0';
        slidesContainer.style.boxSizing = 'border-box';
        slidesContainer.style.justifyContent = 'flex-start';

        const slidePromises = sliderActiveRules.map((promo, index) => {
            const pData = product_results[allRules.indexOf(promo)];
            return this.renderSlide(promo, cart, pData.data, index);
        });

        const slides = await Promise.all(slidePromises);
        slides.forEach(slide => slidesContainer.appendChild(slide));

        const rawWrapperWidth = wrapperDiv ? wrapperDiv.offsetWidth : 0;
        const rawContainerWidth = container ? container.offsetWidth : 0;

        let finalWidth = 0;

        if (rawWrapperWidth > 100) {
            finalWidth = rawWrapperWidth - 40;
        } else if (rawContainerWidth > 100) {
            finalWidth = rawContainerWidth - 40;
        } else {
            finalWidth = window.innerWidth - 80;
        }

        if (finalWidth < 300) finalWidth = 300;
        if (finalWidth > 800) finalWidth = 800;

        let slideCount = sliderActiveRules.length;
        if (slideCount > 4) slideCount = 4;

        const GAP = 4;
        const availableSpace = finalWidth - (GAP * (slideCount - 1));
        let calculatedSlideWidth = Math.floor(availableSpace / slideCount);

        if (calculatedSlideWidth < 70) {
            calculatedSlideWidth = Math.floor(finalWidth / Math.min(sliderActiveRules.length, 4));
        }

        this.slideWidthPx = calculatedSlideWidth;
        this.totalSlidesCount = sliderActiveRules.length;
        slidesContainer.style.width = `${sliderActiveRules.length * this.slideWidthPx}px`;

        slides.forEach(slide => {
            slide.style.flex = `0 0 ${this.slideWidthPx}px`;
            slide.style.width = `${this.slideWidthPx}px`;
            slide.style.flexShrink = '0';
            slide.style.boxSizing = 'border-box';
        });

        this.updateButtonVisibility();
        if (sliderActiveRules.length > 1) this.updateSliderUI(sliderActiveRules.length);
        wrapperDiv.appendChild(slidesContainer);

        // 🆕 TOUCH EVENT HANDLERS — enable drag/swipe navigation (desktop + mobile)
        this._setupTouchEvents(wrapperDiv, slidesContainer);

        // 🆕 MOBILE COLLAPSIBLE — heading + slider wrapped in one toggleable div.
        const collapsible = document.createElement('div');
        collapsible.className = 'saison-gwp-collapsible';
        collapsible.appendChild(heading);
        collapsible.appendChild(wrapperDiv);
        collapsible.style.display = this.sliderCollapsed ? 'none' : 'block';

        currentWrapper.style.position = 'relative';
        currentWrapper.appendChild(collapsible);

        this._applyMobileCollapsePadding(currentWrapper);

        // 🆕 PROGRESS INDICATOR — full-width bar at ABSOLUTE BOTTOM of widget (ALL viewports)
        if (this.totalSlidesCount > 4) {
          const progressContainer = document.createElement('div');
          Object.assign(progressContainer.style, {
            width: '100%',
            height: '4px',
            position: 'absolute',
            bottom: '0',
            left: '0',
            margin: '0',
            cursor: 'pointer',
            zIndex: '5',
            pointerEvents: 'auto'
          });

          // Grey track line (full width, exact match to container)
          const trackLine = document.createElement('div');
          trackLine.style.width = '100%';
          trackLine.style.height = '100%';
          trackLine.style.background = '#dfdfdf';
          trackLine.style.position = 'relative';
          trackLine.style.overflow = 'hidden';
          trackLine.style.left = '0';
          progressContainer.appendChild(trackLine);

          // Draggable rectangular handle - FIXED SIZE, slides within track bounds
          const progressHandle = document.createElement('div');
          progressHandle.className = 'saison-gwp-progress-handle';
          Object.assign(progressHandle.style, {
            width: '60px',                 // Fixed width
            height: '4px',                 // Same height as track
            background: '#000000',         // Solid black
            position: 'absolute',
            top: '0',
            left: '0',                     // Will be updated in pixels
            cursor: 'grab',
            zIndex: '10',
            transition: 'left 0.1s ease',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            touchAction: 'none'
          });

          trackLine.appendChild(progressHandle);
          currentWrapper.appendChild(progressContainer);

          // Store reference for updates
          this._progressElements = { progressHandle, trackLine };

          // Initialize progress indicator
          this._updateProgressIndicator = () => {
            if (!this._progressElements) return;
            
            const { progressHandle, trackLine } = this._progressElements;
            const maxSlide = this.totalSlidesCount - 4;
            const progressPercent = maxSlide > 0 ? this.currentSlide / maxSlide : 0;
            
            // Get actual dimensions
            const trackWidth = trackLine.offsetWidth;
            const handleWidth = 60; // Match the CSS width
            
            // Calculate position - ensure handle stays fully within track
            const maxLeft = trackWidth - handleWidth;
            const pixelPos = Math.max(0, Math.min(maxLeft * progressPercent, maxLeft));
            
            progressHandle.style.left = `${pixelPos}px`;
            progressHandle.style.transform = 'none';
          };

          // Mouse drag handler (desktop)
          let isDragging = false;

          const startDrag = (e) => {
            e.stopPropagation();
            e.preventDefault();
            isDragging = true;
            progressHandle.style.cursor = 'grabbing';
          };

          const doDrag = (e) => {
            if (!isDragging) return;
            e.stopPropagation();
            e.preventDefault();
            
            const rect = trackLine.getBoundingClientRect();
            const x = e.clientX - rect.left;
            
            const handleWidth = 60;
            const maxLeft = rect.width - handleWidth;
            const clampedX = Math.max(0, Math.min(x - (handleWidth / 2), maxLeft));
            const progressPercent = maxLeft > 0 ? clampedX / maxLeft : 0;
            
            const maxSlide = this.totalSlidesCount - 4;
            this.currentSlide = Math.round(progressPercent * maxSlide);
            this._updateSliderPosition();
          };

          const endDrag = () => {
            if (!isDragging) return;
            isDragging = false;
            progressHandle.style.cursor = 'grab';
          };

          progressHandle.addEventListener('mousedown', startDrag);
          document.addEventListener('mousemove', doDrag);
          document.addEventListener('mouseup', endDrag);

          // Click anywhere on track to jump
          trackLine.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            const rect = trackLine.getBoundingClientRect();
            const handleWidth = 60;
            const x = e.clientX - rect.left;
            
            const maxLeft = rect.width - handleWidth;
            const clampedX = Math.max(0, Math.min(x, maxLeft));
            const progressPercent = maxLeft > 0 ? clampedX / maxLeft : 0;
            
            const maxSlide = this.totalSlidesCount - 4;
            this.currentSlide = Math.round(progressPercent * maxSlide);
            this._updateSliderPosition();
          });

          // Touch drag (mobile)
          progressHandle.addEventListener('touchstart', (e) => {
            e.stopPropagation();
            e.preventDefault();
            isDragging = true;
          }, { passive: false });

          progressHandle.addEventListener('touchmove', (e) => {
            if (!isDragging) return;
            e.stopPropagation();
            e.preventDefault();
            
            const touchX = e.touches[0].clientX;
            const rect = trackLine.getBoundingClientRect();
            const handleWidth = 60;
            const x = touchX - rect.left;
            
            const maxLeft = rect.width - handleWidth;
            const clampedX = Math.max(0, Math.min(x - (handleWidth / 2), maxLeft));
            const progressPercent = maxLeft > 0 ? clampedX / maxLeft : 0;
            
            const maxSlide = this.totalSlidesCount - 4;
            this.currentSlide = Math.round(progressPercent * maxSlide);
            this._updateSliderPosition();
          }, { passive: false });

          progressHandle.addEventListener('touchend', () => {
            isDragging = false;
          });
        }

        // 🆕 Toggle pill, centered on the widget's bottom edge (mobile only).
        if (isMobileViewport) {
          const toggleBtn = document.createElement('button');
          toggleBtn.type = 'button';
          toggleBtn.className = 'saison-gwp-toggle';
          Object.assign(toggleBtn.style, {
            position: 'absolute',
            bottom: '0',
            left: '50%',
            transform: 'translate(-50%, 50%)',
            background: '#A4C4D2',
            border: 'none',
            borderRadius: '30px',
            padding: '8px 18px',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: '"DM Sans", sans-serif',
            color: '#ffffffff',
            cursor: 'pointer',
            zIndex: '50',
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          });

          const paintToggle = () => {
            if (this.sliderCollapsed) {
              toggleBtn.innerHTML = 'Choose your free gift';
              toggleBtn.setAttribute('aria-expanded', 'false');
            } else {
              toggleBtn.innerHTML = 'Hide free gift menu';
              toggleBtn.setAttribute('aria-expanded', 'true');
            }
          };

          toggleBtn.onmouseover = () => {
            toggleBtn.style.background = '#000000ff';
            toggleBtn.style.color = '#FFFFFF';
          };
          toggleBtn.onmouseout = () => {
            toggleBtn.style.background = '#A4C4D2';
            toggleBtn.style.color = '#ffffffff';
          };

          toggleBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.sliderCollapsed = !this.sliderCollapsed;
            collapsible.style.display = this.sliderCollapsed ? 'none' : 'block';
            this._applyMobileCollapsePadding(currentWrapper);
            paintToggle();
          };

          paintToggle();
          currentWrapper.appendChild(toggleBtn);
        }

      } else if (hasGWPInCart) {
          console.log('Skipping slider - GWP in cart');
      } else if (hasDOMDiscount) {
          console.log('Skipping slider - discount detected in DOM');
      } else {
          console.log('Skipping slider - no active rules');
      }

      // STYLE LOCK — multiple passes
      const applyStyleLock = (delay) => {
        setTimeout(() => {
          const checkContainer = document.getElementById('gwp-container');
          const checkWrapper = document.getElementById('gwp-protected-content');

          if (checkContainer && checkWrapper) {
            checkContainer.style.setProperty('display', 'block', 'important');
            checkContainer.style.setProperty('visibility', 'visible', 'important');
            checkContainer.style.setProperty('opacity', '1', 'important');

            checkWrapper.style.setProperty('display', 'block', 'important');
            checkWrapper.style.setProperty('visibility', 'visible', 'important');
            checkWrapper.style.setProperty('opacity', '1', 'important');

            const pb = checkWrapper.querySelector('#gwp-progress-bar-container');
            if (pb) {
              pb.style.setProperty('display', 'block', 'important');
              pb.style.setProperty('visibility', 'visible', 'important');
              pb.style.setProperty('opacity', '1', 'important');
              pb.style.setProperty('height', '20px', 'important');

              const track = pb.firstElementChild;
              if (track) {
                track.style.setProperty('visibility', 'visible', 'important');
                track.style.setProperty('opacity', '1', 'important');
              }
            }

            console.log(`✅ Style lock ${delay}ms applied`);
          }
        }, delay);
      };

      applyStyleLock(50);
      applyStyleLock(150);
      applyStyleLock(300);
      applyStyleLock(500);

      // Survival checks
      setTimeout(() => {
        const wrapper = document.getElementById('gwp-protected-content');
        if (!wrapper) {
          console.log('⚠️ AGGRESSIVE CHECK: Wrapper died after render! Re-rendering...');
          if (!this.isProcessing && !this.watchdogRunning) {
            this.checkAndRenderGWPs();
          }
        } else {
          console.log('✅ AGGRESSIVE CHECK: Wrapper survived');
        }
      }, 700);

      setTimeout(() => {
        const wrapper = document.getElementById('gwp-protected-content');
        if (!wrapper) {
          console.log('⚠️ SURVIVAL CHECK 1000ms: Wrapper dead! Re-rendering...');
          if (!this.isProcessing && !this.watchdogRunning) {
            this.checkAndRenderGWPs();
          }
        } else {
          console.log('✅ SURVIVAL CHECK 1000ms: Wrapper alive');
        }
      }, 1000);

      console.log(`✅ Saison GWP: Render complete. Wrapper children: ${currentWrapper?.children.length}`);

    } catch (error) {
      console.error('Saison GWP Error:', error);
    } finally {
      this.isProcessing = false;
      this.isUpdatingDOM = false;
      this.debounceTimer = null;

      if (this.needsRender) {
        this.needsRender = false;
        setTimeout(() => this.checkAndRenderGWPs(), 100);
      }
    }
  }

  // 🆕 Stable identifier for a rule, used to memoise its random seed.
  _ruleKey(rule) {
    return `${rule.id ?? ''}|${rule.gwp_product_handle ?? ''}|${rule.rule_title ?? rule.title ?? ''}`;
  }

  // 🆕 Fisher–Yates-ish stable shuffle: each rule gets a random seed the first
  // time it's seen on this page load; ordering is derived from those seeds.
  _shuffledRules(rules) {
    return rules
      .map(rule => {
        const key = this._ruleKey(rule);
        if (!this._ruleSeeds.has(key)) {
          this._ruleSeeds.set(key, Math.random());
        }
        return { rule, seed: this._ruleSeeds.get(key) };
      })
      .sort((a, b) => a.seed - b.seed)
      .map(({ rule }) => rule);
  }

  renderProgressBar(cartTotalCents, thresholds, hasSaleDiscount = false) {
    if (!thresholds || thresholds.length === 0) return null;

    // 🆕 Hide progress bar ONLY when sale discount exists AND free shipping is met
    const shippingThresholdsForCheck = thresholds.filter(t => t.type === 'shipping');
    const freeShipMet = shippingThresholdsForCheck.some(t => t.isActive);

    if (hasSaleDiscount && freeShipMet) {
      console.log('🏷️ Sale discount + free shipping met — hiding progress bar');
      return null;
    }

    const sortedThresholds = [...thresholds].sort((a, b) => a.amount - b.amount);

    // 🆕 ANCHORED LOCALISED VIEW
    const passed = sortedThresholds.filter(t => t.amount <= cartTotalCents);
    const upcoming = sortedThresholds.filter(t => t.amount > cartTotalCents);
    const anchor = passed.length > 0 ? passed[passed.length - 1].amount : 0;
    const MAX_UPCOMING = 4;
    const selected = upcoming.slice(0, MAX_UPCOMING);

    // 🆕 SPECIAL CASE: When sale discount is active, GWP icons won't render.
    // Find the shipping threshold and use it as viewMax (ignore distant GWPs).
    const shippingThreshold = upcoming.find(t => t.type === 'shipping');
    const allNonShippingAreGWP = upcoming.filter(t => t.type !== 'shipping').every(t => t.type === 'gwp');
    const onlyShippingVisible = hasSaleDiscount && shippingThreshold && allNonShippingAreGWP;
    
    let viewMin = anchor;
    let viewMax = selected.length > 0
      ? (onlyShippingVisible 
          ? Math.round(shippingThreshold.amount * 1.05) // Tight fit to shipping
          : Math.round(selected[selected.length - 1].amount * 1.05)) // Normal: extend to last selected
      : cartTotalCents + 10000;

    if (cartTotalCents <= viewMin) {
      viewMin = Math.max(0, cartTotalCents - 2500);
      if (viewMax <= viewMin) viewMax = viewMin + 10000;
    }

    // 🛡️ Guarantee the cart is always INSIDE the window with visible fill —
    // if the cart sits at (or below) the left edge, pull the view down a bit
    if (cartTotalCents <= viewMin) {
      viewMin = Math.max(0, cartTotalCents - 2500);
      if (viewMax <= viewMin) viewMax = viewMin + 10000;
    }

    const rangeWidth = viewMax - viewMin;
    if (rangeWidth <= 0) return null;

    const visibleThresholds = sortedThresholds.filter(
      t => t.amount >= viewMin && t.amount <= viewMax
    );

    if (visibleThresholds.length === 0) return null;

    console.log(`🔍 Progress bar window: $${viewMin / 100} – $${viewMax / 100} (cart at $${cartTotalCents / 100}, ${visibleThresholds.length} icons)`);

    const container = document.createElement('div');
    container.id = 'gwp-progress-bar-container';
    container.style.width = '100%';
    container.style.marginTop = '0';
    container.style.marginBottom = '10px';
    container.style.position = 'relative';
    container.style.height = '20px';
    container.style.padding = '0 30px';
    container.style.boxSizing = 'border-box';

    const track = document.createElement('div');
    track.style.width = '100%';
    track.style.height = '4px';
    track.style.backgroundColor = '#ffffffff';
    track.style.borderRadius = '2px';
    track.style.position = 'relative';
    track.style.overflow = 'hidden';
    container.appendChild(track);

    const fill = document.createElement('div');
    fill.style.height = '100%';
    fill.style.backgroundColor = '#222321';
    fill.style.borderRadius = '2px';
    fill.style.transition = 'width 0.3s ease';
    fill.style.zIndex = '2';

    let percentage = ((cartTotalCents - viewMin) / rangeWidth) * 100;
    percentage = Math.max(0, Math.min(100, percentage));
    fill.style.width = `${percentage}%`;
    track.appendChild(fill);

    const SVG_URL = 'https://cdn.shopify.com/s/files/1/0072/8514/5709/files/featured_seasonal_and_gifts_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.svg?v=1778214373';
    const TRACK_COLOR = '#ffffffff';
    const BRAND_COLOR = '#222321';

    const iconsToRender = [];
    const ICON_WIDTH_PERCENT = 8;
    const MIN_GAP_PERCENT = 4;

    visibleThresholds.forEach((thresh) => {
      if (hasSaleDiscount && thresh.type === 'gwp') return;

      let pos = ((thresh.amount - viewMin) / rangeWidth) * 100;
      if (pos < 5) pos = 5;
      if (pos > 95) pos = 95;

      if (iconsToRender.length > 0) {
        const lastIcon = iconsToRender[iconsToRender.length - 1];
        const distance = pos - lastIcon.pos;
        const requiredSpace = ICON_WIDTH_PERCENT + MIN_GAP_PERCENT;

        if (distance < requiredSpace) {
          if (thresh.type === 'shipping') {
            iconsToRender.pop();
            iconsToRender.push({ thresh, pos });
          } else if (lastIcon.thresh.type === 'shipping') {
            pos = lastIcon.pos + requiredSpace;
            if (pos > 95) pos = 95;
            iconsToRender.push({ thresh, pos });
          } else {
            iconsToRender.push({ thresh, pos });
          }
        } else {
          iconsToRender.push({ thresh, pos });
        }
      } else {
        iconsToRender.push({ thresh, pos });
      }
    });

    iconsToRender.forEach((item) => {
      const { thresh, pos } = item;

      const iconContainer = document.createElement('div');
      iconContainer.style.position = 'absolute';
      iconContainer.style.top = '-12px';
      iconContainer.style.zIndex = '10';
      iconContainer.style.left = `calc(40px + (${pos} * (99% - 70px) / 100))`;
      iconContainer.style.transform = 'translateX(-50%)';

      const iconCircle = document.createElement('div');
      iconCircle.style.width = '32px';
      iconCircle.style.height = '32px';
      iconCircle.style.borderRadius = '50%';
      iconCircle.style.display = 'flex';
      iconCircle.style.alignItems = 'center';
      iconCircle.style.justifyContent = 'center';
      iconCircle.style.boxShadow = '0 2px 4px rgba(255, 255, 255, 0.2)';
      iconCircle.style.transition = 'all 0.3s ease';
      iconCircle.style.zIndex = 10;

      const tooltip = document.createElement('div');
      tooltip.textContent = this.formatMoney(thresh.amount);
      tooltip.style.position = 'absolute';
      tooltip.style.bottom = '45px';
      tooltip.style.left = '50%';
      tooltip.style.transform = 'translateX(-50%)';
      tooltip.style.background = '#222321';
      tooltip.style.color = '#ffffff';
      tooltip.style.padding = '6px 10px';
      tooltip.style.borderRadius = '4px';
      tooltip.style.fontSize = '12px';
      tooltip.style.fontFamily = '"DM Sans", sans-serif';
      tooltip.style.whiteSpace = 'nowrap';
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
      tooltip.style.transition = 'opacity 0.2s ease, visibility 0.2s ease';
      tooltip.style.pointerEvents = 'none';
      tooltip.style.zIndex = '20';

      iconContainer.onmouseenter = () => {
        tooltip.style.opacity = '1';
        tooltip.style.visibility = 'visible';
      };
      iconContainer.onmouseleave = () => {
        tooltip.style.opacity = '0';
        tooltip.style.visibility = 'hidden';
      };

      iconContainer.appendChild(iconCircle);
      iconContainer.appendChild(tooltip);

      if (thresh.isActive) {
        iconCircle.style.backgroundColor = BRAND_COLOR;
        iconCircle.style.border = 'none';
      } else {
        iconCircle.style.backgroundColor = TRACK_COLOR;
        iconCircle.style.border = `2px solid ${TRACK_COLOR}`;
      }

      if (thresh.type === 'shipping') {
        iconCircle.textContent = '⛟';
        iconCircle.style.fontSize = '16px';
        iconCircle.style.color = thresh.isActive ? '#FFFFFF' : BRAND_COLOR;
      } else {
        const img = document.createElement('img');
        img.src = SVG_URL;
        img.style.width = '16px';
        img.style.height = '16px';
        img.style.display = 'block';
        img.style.filter = thresh.isActive ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(15%)';
        iconCircle.appendChild(img);
      }

      iconContainer.appendChild(iconCircle);
      container.appendChild(iconContainer);
    });

    return container;
  }

  stopSlider() {
    if (this.sliderInterval) {
      clearInterval(this.sliderInterval);
      this.sliderInterval = null;
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // 🆕 getNextRewardMessage now accepts hasSaleDiscount flag
  // If true, suppresses ALL GWP messaging (shipping messages still shown)
  getNextRewardMessage(cartTotalCents, thresholds, isNzCustomer, hasSaleDiscount = false) {
    if (!thresholds || thresholds.length === 0) {
      return { shippingMsg: {show: false, text: ''}, gwpMsg: {show: false, text: ''} };
    }

    let shippingMsg = { show: false, text: '' };

    if (!isNzCustomer) {
      const shippingThreshold = thresholds.find(t => t.type === 'shipping');

      if (shippingThreshold) {
        if (shippingThreshold.isActive) {
          shippingMsg = {
            show: true,
            text: '<span style="color: #A4C4D2; font-weight: 700;">You have qualified for FREE shipping.</span>'
          };
        } else {
          const remaining = shippingThreshold.amount - cartTotalCents;
          if (remaining > 0) {
            shippingMsg = {
              show: true,
              text: `Get FREE shipping when you spend <strong>${this.formatMoney(remaining)}</strong> more.`
            };
          }
        }
      }
    }

    // 🆕 Suppress ALL GWP messaging if sale discount is active
    if (hasSaleDiscount) {
      console.log('🧸 Sale discount detected — suppressing all GWP messages');
      return { shippingMsg, gwpMsg: {show: false, text: ''} };
    }

    const gwpThresholds = thresholds.filter(t => t.type === 'gwp');
    let gwpMsg = { show: false, text: '' };

    if (gwpThresholds.length > 0) {
      const sortedGwp = [...gwpThresholds].sort((a, b) => a.amount - b.amount);
      let nextUnlockable = null;

      // Find first gift that is NOT yet unlocked
      for (const thresh of sortedGwp) {
        const cartMet = cartTotalCents >= thresh.amount;
        const hasQualifyingTotal = thresh.qualifyingTotal !== undefined && thresh.qualifyingTotal !== null;
        const qualMet = hasQualifyingTotal && thresh.qualifyingTotal >= thresh.amount;

        if (cartMet || qualMet) {
          continue; // this one is unlocked
        }
        nextUnlockable = thresh;
        break;
      }

      if (!nextUnlockable) {
        // All gifts are unlocked - show confirmation
        gwpMsg = { 
          show: true, 
          text: 'You have unlocked all gifts, please make a selection. <a href="https://saison-version-2.myshopify.com/pages/special-offers" style="color: inherit; text-decoration: none;">ⓘ</a>' 
        };
      } else {
        // Some gift is still locked - show spend message
        const spendToCompare = nextUnlockable.qualifyingTotal || 0;
        const remaining = nextUnlockable.amount - spendToCompare;
        const remainingDisplay = this.formatMoney(remaining);
        const giftName = nextUnlockable.productName || 'free gift';

        if (nextUnlockable.sitewide === true) {
          gwpMsg.text = `Spend <strong>${remainingDisplay}</strong> more sitewide to unlock more FREE gift options <a href="https://saison-version-2.myshopify.com/pages/special-offers" style="color: inherit; text-decoration: none;">ⓘ</a>`;
        } else {
          let collectionName = 'collections';
          if (nextUnlockable.showOnCollections && Array.isArray(nextUnlockable.showOnCollections)) {
            const handles = nextUnlockable.showOnCollections;
            if (handles.length === 1) {
              collectionName = handles[0]
                .replace(/-/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            } else if (handles.length <= 2) {
              collectionName = handles.map(h => h.replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).join(' & ');
            } else {
              collectionName = `${handles.length} collections`;
            }
          }
          gwpMsg.text = `Spend <strong>${remainingDisplay}</strong> more on ${collectionName} to qualify for a FREE ${giftName}`;
        }
        gwpMsg.show = true;
      }
    }

    return { shippingMsg, gwpMsg };
  }

  // 🆕 Setup touch event handlers for slider drag/swipe
  _setupTouchEvents(wrapperDiv, slidesContainer) {
    const self = this;
    
    wrapperDiv.addEventListener('touchstart', (e) => {
      self.touchStartX = e.touches[0].clientX;
      self.isDragging = true;
      slidesContainer.style.transition = 'none'; // Disable transition during drag for smoothness
    }, { passive: true });

    wrapperDiv.addEventListener('touchmove', (e) => {
      if (!self.isDragging) return;
      const touchX = e.touches[0].clientX;
      const deltaX = touchX - self.touchStartX;
      
      // Calculate position based on current slide width
      const maxDrag = (self.totalSlidesCount - 4) * (self.slideWidthPx + 4);
      const constrainedDelta = Math.max(-self.currentSlide * (self.slideWidthPx + 4), 
                                        Math.min(deltaX, maxDrag - (self.currentSlide * (self.slideWidthPx + 4))));
      slidesContainer.style.transform = `translateX(-${self.currentSlide * (self.slideWidthPx + 4) - constrainedDelta}px)`;
    }, { passive: true });

    wrapperDiv.addEventListener('touchend', () => {
      if (!self.isDragging) return;
      self.isDragging = false;
      slidesContainer.style.transition = 'transform 0.3s ease-in-out';
      
      self._handleSwipeEnd();
    });

    wrapperDiv.addEventListener('touchcancel', () => {
      self.isDragging = false;
      slidesContainer.style.transition = 'transform 0.3s ease-in-out';
      self._updateSliderPosition();
    });
  }

  // 🆕 Process swipe gesture to determine if we should advance/reverse slide
  _handleSwipeEnd() {
    this.touchEndX = this.touchStartX; // Should be captured properly
    
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) < this.minSwipeDistance) return;
    
    const targetVisibleCount = Math.min(this.totalSlidesCount, 4);
    const maxSlide = this.totalSlidesCount - targetVisibleCount;
    
    if (swipeDistance > 0 && this.currentSlide > 0) {
      // Swiped right → go to previous slide
      this.currentSlide--;
    } else if (swipeDistance < 0 && this.currentSlide < maxSlide) {
      // Swiped left → go to next slide
      this.currentSlide++;
    }
    
    this._updateSliderPosition();
  }

  // 🆕 Update slider position and progress indicator (unified method for all navigation)
  _updateSliderPosition() {
    const container = document.getElementById('gwp-container');
    if (!container) return;
    
    const slidesContainer = container.querySelector('.saison-gwp-slides');
    if (slidesContainer) {
      const slideUnitWidth = this.slideWidthPx + 4;
      slidesContainer.style.transform = `translateX(-${this.currentSlide * slideUnitWidth}px)`;
    }
    
    this.updateButtonVisibility();
    
    // Update progress indicator if it exists
    if (this._updateProgressIndicator) {
      this._updateProgressIndicator();
    }
  }

  updateButtonVisibility() {
    if (!this.navPrevBtn || !this.navNextBtn) return;

    const targetVisibleCount = Math.min(this.totalSlidesCount, 4);

    if (this.totalSlidesCount <= targetVisibleCount) {
      this.navPrevBtn.style.display = 'none';
      this.navNextBtn.style.display = 'none';
      return;
    }

    this.navPrevBtn.style.display = this.currentSlide > 0 ? 'block' : 'none';

    const maxSlideIndex = this.totalSlidesCount - targetVisibleCount;

    this.navNextBtn.style.display = this.currentSlide < maxSlideIndex ? 'block' : 'none';
  }

  nextSlide(totalSlides) {
    const targetVisibleCount = Math.min(totalSlides, 4);
    const maxSlide = totalSlides - targetVisibleCount;

    if (this.currentSlide >= maxSlide) return;

    this.currentSlide++;
    this._updateSliderPosition();
  }

  prevSlide(totalSlides) {
    if (this.currentSlide <= 0) return;

    this.currentSlide--;
    this._updateSliderPosition();
  }

  updateSliderUI(totalSlides) {
    this._updateSliderPosition();
  }

  parseMinSpend(val) {
    if (typeof val === 'number') return val;
    const d = parseFloat(val);
    return isNaN(d) ? 0 : Math.round(d * 100);
  }

  // GWP items never count toward their own qualification threshold.
  calculateQualifyingTotal(cart, targetVendor, isSitewide) {
    const eligibleItems = cart.items.filter(
      item => !(item.properties && item.properties._gwp_item === 'true')
    );

    if (isSitewide) {
      return eligibleItems.reduce((total, item) => total + (item.final_line_price || 0), 0);
    }

    if (!targetVendor || targetVendor.trim() === '') {
      return 0;
    }

    const targetLower = targetVendor.trim().toLowerCase();

    return eligibleItems.reduce((total, item) => {
      const itemVendor = item.vendor ? item.vendor.trim().toLowerCase() : '';
      if (itemVendor.includes(targetLower)) {
        return total + (item.final_line_price || 0);
      }
      return total;
    }, 0);
  }

  isGWPInCart(cart) {
    return cart.items.some(item => item.properties && item.properties._gwp_item === 'true');
  }

  clearGWPs() {
    const container = document.getElementById('gwp-container');
    if (container) {
      container.innerHTML = '';
      this.renderedIds.clear();
      this.stopSlider();
      this.navPrevBtn = null;
      this.navNextBtn = null;
      this._progressElements = null;
      this._updateProgressIndicator = null;
    }
  }

  renderSlide(promo, cart, productData, index) {
    return new Promise((resolve) => {
      const product = productData.product;
      let variant = product.variants[0];
      let imageUrl = null;
      if (variant.image?.src) imageUrl = variant.image.src;
      else if (product.images?.[0]?.src) imageUrl = product.images[0].src;
      else imageUrl = 'https://via.placeholder.com/80?text=No+Image';

      const slide = document.createElement('div');
      slide.className = 'saison-gwp-slide';
      slide.style.boxSizing = 'border-box';
      slide.style.margin = '0 2px';
      slide.style.padding = '0 5px 0 5px';
      slide.style.display = 'flex';
      slide.style.flexDirection = 'column';
      slide.style.alignItems = 'center';
      slide.style.justifyContent = 'center';
      slide.style.textAlign = 'center';
      slide.style.height = '100%';

      const ruleTitle = promo.rule_title || promo.title || "GWP Offer";
      const vendorName = product.vendor || "";

      const contentWrapper = document.createElement('div');
      contentWrapper.style.display = 'flex';
      contentWrapper.style.flexDirection = 'column';
      contentWrapper.style.alignItems = 'center';
      contentWrapper.style.justifyContent = 'center';
      contentWrapper.style.textAlign = 'center';
      contentWrapper.style.width = '100%';
      contentWrapper.style.margin = '0';
      contentWrapper.style.padding = '0';

      const imgContainer = document.createElement('div');
      imgContainer.style.position = 'relative';
      imgContainer.style.width = '70px';
      imgContainer.style.height = '70px';
      imgContainer.style.marginBottom = '10px';

      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = variant.title;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.filter = 'brightness(0.93) contrast(1.1)';
      img.style.borderRadius = '12px';
      img.style.display = 'block';

      let vendorEl = null;
      if (vendorName) {
        vendorEl = document.createElement('div');
        vendorEl.textContent = vendorName.toUpperCase();
        vendorEl.style.fontSize = '11px';
        vendorEl.style.color = '#888';
        vendorEl.style.marginBottom = '0';
        vendorEl.style.textTransform = 'uppercase';
        vendorEl.style.letterSpacing = '0.5px';
      }

      const title = document.createElement('h4');
      title.textContent = ruleTitle;
      title.style.margin = '0 3px 5px 3px';
      title.style.fontSize = '12px';
      title.style.fontWeight = 'normal';
      title.style.lineHeight = '1.4';
      title.style.wordWrap = 'break-word';
      title.style.overflowWrap = 'break-word';
      title.style.fontFamily = 'inherit';

      // 🆕 Hide GWP names on small screens — saves vertical space, product
      // image alone identifies the gift.
      if (this._isMobileViewport()) {
        title.style.display = 'none';
      }

      const btn = document.createElement('button');
      btn.textContent = 'Add to Cart';

      btn.style.position = 'absolute';
      btn.style.top = '50%';
      btn.style.left = '50%';
      btn.style.transform = 'translate(-50%, -50%) scale(0.9)';
      btn.style.opacity = '0';

      btn.style.padding = '15px 20px';
      btn.style.background = '#f8f8f8';
      btn.style.color = '#000000ff';
      btn.style.border = '1px solid #dfdfdf';
      btn.style.borderRadius = '50px';
      btn.style.cursor = 'pointer';
      btn.style.fontSize = '13px';
      btn.style.fontWeight = '500';
      btn.style.boxSizing = 'border-box';
      btn.style.transition = 'all 0.3s ease';
      btn.style.zIndex = '30';

      btn.style.whiteSpace = 'nowrap';
      btn.style.width = 'auto';
      btn.style.minWidth = '100px';
      btn.style.height = 'auto';

      const paintHover = () => {
        btn.style.setProperty('background', '#A4C4D2', 'important');
        btn.style.setProperty('color', '#FFFFFF', 'important');
        btn.style.setProperty('border-color', '#A4C4D2', 'important');
      };
      const paintIdle = () => {
        btn.style.setProperty('background', '#f8f8f8', 'important');
        btn.style.setProperty('color', '#000000ff', 'important');
        btn.style.setProperty('border-color', '#dfdfdf', 'important');
      };

      btn.addEventListener('mouseenter', paintHover);
      btn.addEventListener('mouseleave', paintIdle);

      btn.onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (btn.style.opacity === '0') return;

        btn.disabled = true;
        btn.textContent = 'Adding...';
        paintHover();

        try {
          const response = await fetch('/cart/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              id: variant.id,
              quantity: 1,
              properties: { '_gwp_item': 'true', '_gwp_rule_id': promo.id, '_gwp_rule_title': ruleTitle }
            })
          });
          if (response.ok) {
            setTimeout(() => {
              document.body.dispatchEvent(new CustomEvent('cart:updated'));
              document.body.dispatchEvent(new CustomEvent('cart-update', { bubbles: true }));
              document.body.dispatchEvent(new Event('cart-drawer:refresh', { bubbles: true }));

              console.log('🔥 FORCING GWP re-render after cart add');
              this.checkAndRenderGWPs();
            }, 800);

            btn.textContent = 'Added!';
            setTimeout(() => {
              btn.disabled = false;
              btn.textContent = 'Add to Cart';
              paintIdle();
            }, 2000);
          } else throw new Error('Failed');
        } catch (err) {
          console.error('Cart add failed', err);
          btn.disabled = false;
          btn.textContent = 'Add to Cart';
          paintIdle();
        }
      };

      imgContainer.appendChild(img);
      imgContainer.appendChild(btn);

      contentWrapper.appendChild(imgContainer);

      if (vendorEl) contentWrapper.appendChild(vendorEl);
      contentWrapper.appendChild(title);

      const updateVisibility = (visible) => {
        btn.style.opacity = visible ? '1' : '0';
        btn.style.transform = visible
          ? 'translate(-50%, -50%) scale(1)'
          : 'translate(-50%, -50%) scale(0.9)';
      };

      contentWrapper.addEventListener('mouseenter', () => updateVisibility(true));
      contentWrapper.addEventListener('mouseleave', () => updateVisibility(false));

      slide.appendChild(contentWrapper);
      resolve(slide);
    });
  }
}

// Single, safe boot point — guards against duplicate instantiation.
(function bootSaisonGWP() {
  if (window.__saisonGWPBooted) return;
  window.__saisonGWPBooted = true;

  const boot = () => new SaisonGWPManger();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();